import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import TransformerFilters from '../../components/catalog/TransformerFilters';
import TransformerCard from '../../components/catalog/TransformerCard';
import { TransformerFilters as Filters, Transformer } from '../../types/transformer';
import RelatedCategories from '../../components/catalog/RelatedCategories';
import ContactForm from '../../components/forms/ContactForm';
import { transformers as transformersData } from '../../utils/transformers';

// НОВЫЙ КОМПОНЕНТ TransformerHero
const TransformerHero: React.FC = () => {
  return (
    <section className="relative overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0 min-h-[70vh]">
        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
          <source src="/videos/0510(1).mp4" type="video/mp4" />
          Ваш браузер не поддерживает видео.
        </video>
        {/* Overlay */}
        <div className="absolute inset-0 bg-primary-900/40"></div>
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 pt-16 pb-16 md:pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left column: Text content */}
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight text-white">
              Трансформаторы ТСЛ, ТСЗЛ и ТМГ от Проминнова – надежность и экономия для вашего
              проекта
            </h1>

            <p className="text-xl md:text-2xl text-primary-100 mb-8">
              Сухие и масляные трансформаторы различных типов и мощностей. Производство и поставка с
              быстрой доставкой по РФ.
            </p>

            {/* Особенности трансформаторов */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-start">
                <div className="bg-primary-700 bg-opacity-50 rounded-lg p-2 mr-3">
                  {/* ShieldCheck */}
                </div>
                <div>
                  <p className="font-medium text-white">Высокая надежность</p>
                  <p className="text-sm text-primary-200">Проверенные решения для любых условий</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-primary-700 bg-opacity-50 rounded-lg p-2 mr-3">
                  {/* Lightning */}
                </div>
                <div>
                  <p className="font-medium text-white">Широкий ассортимент</p>
                  <p className="text-sm text-primary-200">ТСЛ, ТСЗЛ, ТМГ и другие типы</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Form */}
          <div>
            <div className="bg-white text-gray-900 rounded-xl shadow-xl overflow-hidden">
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Оставьте заявку</h3>
                <p className="text-gray-600 mb-6">
                  Заполните форму, и наш менеджер свяжется с вами в течение 2 часов (в рабочее
                  время).
                </p>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TransformerListPage: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({
    power: '',
    voltage: '',
    connection: '',
    material: '',
    protection: '',
    type: '',
  });

  const [transformers] = useState<Transformer[]>(transformersData);

  const filteredTransformers = React.useMemo(() => {
    return transformers.filter((t) => {
      const powerMatch =
        !filters.power || (t.power !== null && parseInt(t.power) === parseInt(filters.power));
      const voltageMatch = !filters.voltage || t.voltage === filters.voltage;
      const connectionMatch = !filters.connection || t.connection === filters.connection;
      const materialMatch = !filters.material || t.material === filters.material;
      const protectionMatch = !filters.protection || t.protection === filters.protection;
      const typeMatch = !filters.type || t.type === filters.type;

      return (
        powerMatch &&
        voltageMatch &&
        connectionMatch &&
        materialMatch &&
        protectionMatch &&
        typeMatch
      );
    });
  }, [transformers, filters]);

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  return (
    <>
      <Helmet>
        <title>Каталог трансформаторов ТСЛ и ТСЗЛ – цены и характеристики | Проминнова</title>
        <meta
          name="description"
          content="Сухие трансформаторы ТСЛ и ТСЗЛ от 40 до 2500 кВА, 6/0.4 и 10/0.4 кВ. Литая изоляция, алюминиевые и медные обмотки, защитные кожухи IP21/IP31. Гарантия 5 лет. Купить с доставкой."
        />
        <meta
          name="keywords"
          content="трансформаторы ТСЛ, трансформаторы ТСЗЛ, сухие трансформаторы, литая изоляция, купить трансформатор, ТСЛ 250 кВА, ТСЛ 400 кВА, ТСЛ 630 кВА, ТСЛ 1000 кВА, ТСЗЛ с кожухом"
        />
      </Helmet>

      <TransformerHero />

      <div className="container-custom py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">
            Пром Инновации производит любые типы трансформаторов под потребности и ТЗ заказчика
          </h2>
        </div>

        <TransformerFilters
          selectedPower={filters.power}
          selectedVoltage={filters.voltage}
          selectedConnection={filters.connection}
          selectedMaterial={filters.material}
          selectedProtection={filters.protection}
          selectedType={filters.type}
          onFilterChange={handleFilterChange}
        />

        {filteredTransformers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredTransformers.map((transformer) => (
              <TransformerCard key={transformer.id} transformer={transformer} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-gray-600">
              По вашему запросу трансформаторы не найдены. Попробуйте изменить параметры фильтрации.
            </p>
            <button
              onClick={() =>
                setFilters({
                  power: '',
                  voltage: '',
                  connection: '',
                  material: '',
                  protection: '',
                  type: '',
                })
              }
              className="mt-4 btn-primary"
            >
              Сбросить фильтры
            </button>
          </div>
        )}

        <RelatedCategories currentCategory="transformers" />

        {/* SEO текст */}
        <div className="mt-16 bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-2xl font-semibold mb-4">
            Трансформаторы ТСЛ и ТСЗЛ – надежное и экономичное решение
          </h2>
          <div className="prose max-w-none text-gray-600">
            <p>
              Трансформаторы сухие литые (ТСЛ) и сухие защищенные литые (ТСЗЛ) от компании
              «Проминнова» – современное решение для энергоснабжения промышленных и гражданских
              объектов. Наши трансформаторы отличаются следующими преимуществами:
            </p>

            <ul className="mt-4">
              <li>Высокая надежность и длительный срок службы (30+ лет)</li>
              <li>Экономичность – снижение затрат на электроэнергию на 5-10%</li>
              <li>Пожаробезопасность и экологичность благодаря отсутствию масла</li>
              <li>Компактные размеры и малый вес по сравнению с масляными аналогами</li>
              <li>Простота монтажа и минимальные требования к обслуживанию</li>
              <li>Тепловая защита с датчиками температуры Pt100</li>
              <li>Гарантия 5 лет с возможностью продления</li>
              <li>
                ТСЗЛ с защитным кожухом IP21/IP31 для установки в помещениях с повышенной влажностью
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-8 mb-4">Области применения</h3>
            <ul className="mt-4">
              <li>Промышленные предприятия и производственные цеха</li>
              <li>Коммерческие объекты и торговые центры</li>
              <li>Административные здания и офисные центры</li>
              <li>Жилые комплексы и микрорайоны</li>
              <li>Объекты инфраструктуры и коммунального хозяйства</li>
              <li>Энергетические объекты и подстанции</li>
            </ul>

            <h3 className="text-xl font-semibold mt-8 mb-4">Преимущества работы с нами</h3>
            <ul className="mt-4">
              <li>Собственное производство трансформаторов</li>
              <li>Строгий контроль качества на всех этапах</li>
              <li>Индивидуальный подход к каждому клиенту</li>
              <li>Гибкая система скидок и специальных предложений</li>
              <li>Быстрая доставка по всей России</li>
              <li>Профессиональный монтаж и пусконаладка</li>
              <li>Сервисное обслуживание и ремонт</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransformerListPage;
